/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';
import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { TicketEditService } from './ticket-edit.service';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IPreviousAssignedProjectMember } from '@touchplan/common';
import { CONSTRAINT_ACCEPTANCE_STATE } from '@touchplan/common';
import { ProjectMember } from '@touchplan/common';

@Injectable()
export class ConstraintAcceptanceService {
	private projectMembersSync: Map<string, ProjectMember> = new Map();
	private shutdownSubject: Subject<boolean> = new Subject();
	constructor(private ticketEditService: TicketEditService, private planState: PlanState) {
		planState.stateChange$.subscribe((stateEvent: StateEvents) => {
			switch (stateEvent) {
				case StateEvents.FULLY_LOADED:
					this.generateWatchers();
					break;
				case StateEvents.BOOMED:
					this.destroy();
					break;
				default:
					break;
			}
		});
	}

	private generateWatchers(): void {
		this.planState.projectMembersApi.data.list$.pipe(takeUntil(this.shutdownSubject)).subscribe((pmList) => {
			this.projectMembersSync = pmList;
		});
	}

	private destroy(): void {
		this.shutdownSubject.next(true);
	}

	public setAssignedProjectMember(
		combined: EditTicketsCombined,
		assignedProjectMemberId: string,
		isReassignment: boolean = false
	): void {
		const newData = {};
		const newPm = this.projectMembersSync.get(assignedProjectMemberId);
		const newAssignedProjectMemberRoleId: string = newPm && newPm.view.roleId ? newPm.view.roleId : null;
		newData['assignedProjectMemberId'] = assignedProjectMemberId || null;
		newData['assignedProjectMemberRoleId'] = newAssignedProjectMemberRoleId;

		// This sets a new requestedByProjectMember if the current assigned to re-assigns the constraint
		if (
			isReassignment &&
			combined.data.liveAssignedProjectMemberId &&
			combined.ticketsInPlanRegion &&
			combined.data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED
		) {
			// Uncomment this if we want to recalc primary constraint role during re-assignment
			const oldPm = this.projectMembersSync.get(combined.data.liveAssignedProjectMemberId);
			const oldAssignedProjectMemberRoleId: string = oldPm && oldPm.view.roleId ? oldPm.view.roleId : null;
			const primaryRoleId = combined.data.assignedProjectMemberRoleId
				? combined.data.assignedProjectMemberRoleId
				: oldAssignedProjectMemberRoleId;
			newData['requestedByProjectMemberId'] = combined.data.liveAssignedProjectMemberId;
			newData['requestedByProjectMemberRoleId'] = primaryRoleId; //combined.data.assignedProjectMemberRoleId;
		}

		const newPreviousProjectMemberRoleId: string = newPm && newPm.view.roleId ? newPm.view.roleId : null;
		const primaryRoleId = combined.data.assignedProjectMemberRoleId
			? combined.data.assignedProjectMemberRoleId
			: newPreviousProjectMemberRoleId;
		const listOfAssignments: IPreviousAssignedProjectMember[] =
			combined.data.previousAssignedProjectMembers || [];
		listOfAssignments.push({
			assignedProjectMemberId: assignedProjectMemberId || null,
			requestedByProjectMemberId:
				newData['requestedByProjectMemberId'] ||
				combined.data.requestedByProjectMemberId ||
				assignedProjectMemberId,
			roleId: primaryRoleId,
			reassignedAt: fbdbTime.syncTime,
		});
		newData['previousAssignedProjectMembers'] = listOfAssignments;

		this.ticketEditService.writeCombinationBackToList(combined, newData);
	}

	public setRequestedByProjectMemberId(
		combined: EditTicketsCombined,
		requestedByProjectMemberId: string
	): void {
		const newData = {};
		const newPm = this.projectMembersSync.get(requestedByProjectMemberId);
		const requestedByProjectMemberRoleId: string = newPm && newPm.view.roleId ? newPm.view.roleId : null;
		newData['requestedByProjectMemberId'] = requestedByProjectMemberId;
		newData['requestedByProjectMemberRoleId'] = requestedByProjectMemberRoleId;

		this.ticketEditService.writeCombinationBackToList(combined, newData);
	}

	public setAccepted(combined: EditTicketsCombined): void {
		const newData = {};
		const newPm = this.projectMembersSync.get(combined.data.assignedProjectMemberId);
		const newAssignedProjectMemberRoleId: string = newPm && newPm.view.roleId ? newPm.view.roleId : null;
		const newResponsibleRole = newPm && newPm.view.role ? newPm.view.role : null;

		const primaryRoleId = combined.data.assignedProjectMemberRoleId
			? combined.data.assignedProjectMemberRoleId
			: newAssignedProjectMemberRoleId;
		const primaryRole = combined.data.assignedProjectMemberRole
			? combined.data.assignedProjectMemberRole
			: newResponsibleRole;

		newData['assignedProjectMemberId'] = null;
		newData['assignedProjectMemberRoleId'] = null;
		newData['responsibleProjectMemberId'] = combined.data.assignedProjectMemberId;
		newData['responsibleProjectMemberRoleId'] = primaryRoleId;
		newData['responsibleProjectMemberRole'] = primaryRole;
		this.ticketEditService.writeCombinationBackToList(combined, newData);
	}

	public setUnassigned(combined: EditTicketsCombined): void {
		const newData = {};

		const primaryRoleId = combined.data.requestedByProjectMemberId
			? combined.data.requestedByProjectMemberRoleId
			: null;

		newData['assignedProjectMemberId'] = null;
		newData['assignedProjectMemberRoleId'] = null;
		newData['responsibleProjectMemberId'] = null;
		newData['responsibleProjectMemberRoleId'] = null;
		newData['responsibleProjectMemberRole'] = null;
		newData['requestedByProjectMemberId'] = combined.data.requestedByProjectMemberId;
		newData['requestedByProjectMemberRoleId'] = primaryRoleId;

		// Reset assigned project member
		const liveAssignedControl = this.ticketEditService.getEditFormControl('liveAssignedProjectMemberId');
		if (liveAssignedControl) {
			liveAssignedControl.setValue(null);
		}

		this.ticketEditService.writeCombinationBackToList(combined, newData);
	}
}
